import StreamService from '../../services/StreamService';
import {
  StreamSessionsByDevice,
  ActiveStreamSessionsResponse,
} from '../../models/StreamSession';

export interface StreamSessionState {
  activeDeviceSessions: StreamSessionsByDevice[];
  /** Prevents re-opening streams on every refresh after the initial restore */
  hasRestoredStreams: boolean;
}

export default {
  state: {
    activeDeviceSessions: [],
    hasRestoredStreams: false,
  } as StreamSessionState,

  mutations: {
    SET_ACTIVE_STREAM_SESSIONS(
      state: StreamSessionState,
      devices: StreamSessionsByDevice[]
    ) {
      state.activeDeviceSessions = devices;
    },
    SET_HAS_RESTORED_STREAMS(state: StreamSessionState) {
      state.hasRestoredStreams = true;
    },
  },

  actions: {
    fetchActiveStreamSessions(context: any) {
      StreamService.getActiveSessions()
        .then((response) => {
          const data: ActiveStreamSessionsResponse = response.data;
          const devices = data.devices || [];
          context.commit('SET_ACTIVE_STREAM_SESSIONS', devices);

          // On first load, auto-open the first device where the
          // current user already has an active session.
          if (!context.state.hasRestoredStreams) {
            context.commit('SET_HAS_RESTORED_STREAMS');
            const userId = context.rootState.user?.id;
            if (userId) {
              const myUids = devices
                .filter((d: StreamSessionsByDevice) =>
                  d.sessions.some((s) => s.userId === userId)
                )
                .map((d: StreamSessionsByDevice) => d.uid);

              if (myUids.length > 0) {
                context.commit('ADD_OPEN_STREAM_UID', myUids[0]);
                context.commit('SET_RT_STREAM_DIALOG', true);
              }
            }
          }
        })
        .catch((error) => {
          console.error('fetchActiveStreamSessions failed:', error);
        });
    },

    async joinStreamSession(context: any, uid: number): Promise<boolean> {
      try {
        await StreamService.joinSession(uid);
        return true;
      } catch (error: any) {
        console.error('joinStreamSession failed:', error);
        context.dispatch('notify', 'Failed to join stream session');
        return false;
      }
    },

    async leaveStreamSession(context: any, uid: number) {
      try {
        await StreamService.leaveSession(uid);
      } catch (error) {
        console.error('leaveStreamSession failed:', error);
      }
    },

    // Signal-only actions for cross-component communication
    streamSessionUpdateReceived(_context: any, _payload: any) {},
    streamRecordingAutoStopped(_context: any, _payload: any) {},
    // EN-1261: someone in the group started or stopped a recording.
    streamRecordingUpdateReceived(_context: any, _payload: any) {},
  },

  getters: {
    hasActiveStreamSessions(state: StreamSessionState): boolean {
      return state.activeDeviceSessions.length > 0;
    },
    activeSessionCount(state: StreamSessionState): number {
      return state.activeDeviceSessions.reduce(
        (sum, d) => sum + d.activeSessionCount,
        0
      );
    },
    sessionForUid:
      (state: StreamSessionState) =>
      (uid: number): StreamSessionsByDevice | undefined => {
        return state.activeDeviceSessions.find((d) => d.uid === uid);
      },
  },
};
